import Phaser from 'phaser';

import { BlocksMainScene } from '../../scenes/blocks-main-scene';
import { GameSizeMetrics } from 'src/games/common/phaser/game/scenes/scaled-base-scene';
import { ShapeViewModel } from './shape-view-model';
import { Subscription } from 'rxjs';
import { BlockView } from './block-view';
// import { DebugContainerVisual } from '../debug/debug-container-visual';
import { delay } from 'src/games/common/helper';

export class ShapeView extends Phaser.GameObjects.Container {
  public get shapeId() {
    return this.viewModel.currentShape.id;
  }

  private subscriptionGroup: Subscription;
  private shapeRectangle: Phaser.GameObjects.Rectangle;
  private blockViews: BlockView[] = [];
  private fadeAnimation: Phaser.Tweens.Tween;
  private bounceAnimation: Phaser.Tweens.Tween;

  // private debugContainerVisual: DebugContainerVisual;
  // private debugLabel: Phaser.GameObjects.Text;

  private scaleFactor = 1;
  private scaledOffsetX = 0;
  private dragStartPos = new Phaser.Geom.Point();
  private dragStartOffset = new Phaser.Geom.Point();
  private dragMaxDistance = 0;
  private targetPosX = 0;
  private targetPosY = 0;
  private hasDragged = false;
  private pointerWasDown = false;

  constructor(
    scene: BlocksMainScene,
    x: number,
    y: number,
    private viewModel: ShapeViewModel,
  ) {
    super(scene, x, y);

    // this.debugContainerVisual = new DebugContainerVisual(scene, this, true);

    this.subscriptionGroup = new Subscription();

    this.shapeRectangle = scene.add.rectangle(0, 0, 0, 0, 0x440000);
    this.shapeRectangle.alpha = 0.0;
    this.add([this.shapeRectangle]);

    // this.debugLabel = scene.add
    //   .text(0, 0, `${viewModel.currentShape.id}`, {
    //     fontSize: '18px',
    //     color: '#ffffff',
    //     backgroundColor: '#000000',
    //   })
    //   .setOrigin(0.0, 0.0);
    // this.add([this.debugLabel]);

    this.setName(ShapeView.name);
    scene.add.existing(this);

    this.blockViews = viewModel.allBlockViewModels.map((vm) => new BlockView(scene, 0, 0, vm));
    this.add([...this.blockViews]);

    this.handleInput();

    this.fadeAnimation = scene.tweens.add({
      targets: this,
      alpha: { from: 0, to: 0.3 },
      duration: 400,
      ease: 'Cubic.Out',
      paused: true,
      onComplete: () => {
        this.alpha = 1;
      },
    });

    this.bounceAnimation = scene.tweens.add({
      targets: this,
      scale: { from: 0.2, to: 1.0 },
      duration: 400,
      ease: 'Back.Out',
      paused: true,
      onComplete: () => {
        this.scale = 1;
      },
    });

    this.alpha = 0;
    this.scale = 0.2;

    this.subscriptionGroup.add(
      this.viewModel.currentDepth$.subscribe((value) => {
        this.depth = value;
      }),
    );

    this.subscriptionGroup.add(
      this.viewModel.isValid$.subscribe((isValid) => {
        if (!isValid) {
          this.disableInteractive();
          return;
        }
        this.resizeShape(false, false);
      }),
    );

    this.subscriptionGroup.add(
      this.viewModel.isDragging$.subscribe((isDragging) => {
        this.resizeShape(isDragging, isDragging);
        this.blockViews.forEach((view) =>
          view.onResize(this.scaleFactor, this.scaledOffsetX, isDragging),
        );
      }),
    );
  }

  override destroy(_fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(_fromScene);
  }

  public onResize(gameSizeMetrics: GameSizeMetrics) {
    const calc = this.viewModel.calculateScaleFactor(gameSizeMetrics);
    this.scaleFactor = calc.scaleFactor;
    this.scaledOffsetX = calc.scaledOffsetX;

    this.blockViews.forEach((view) =>
      view.onResize(this.scaleFactor, this.scaledOffsetX, this.viewModel.currentlyDragging),
    );

    this.resizeShape(this.viewModel.currentlyDragging, true);
  }

  public async animateIn(delayMs: number) {
    await delay(delayMs);

    this.fadeAnimation.play();
    this.bounceAnimation.play();
  }

  public sceneUpdate(time: number, delta: number) {
    if (this.viewModel.currentlyDragging) {
      return;
    }

    const calcPos = this.viewModel.calculateTargetPosition();
    this.targetPosX = calcPos.x * this.scaleFactor + this.scaledOffsetX;
    this.targetPosY = calcPos.y * this.scaleFactor;

    const speed = 0.1;
    const targetDelta = 0.12 * delta * speed;

    this.setPosition(
      this.x * (1 - targetDelta) + this.targetPosX * targetDelta,
      this.y * (1 - targetDelta) + this.targetPosY * targetDelta,
    );
  }

  private resizeShape(isDragging: boolean, setPosition: boolean): void {
    const layoutConfig = this.viewModel.layoutConfig;

    const draggingScaler = isDragging ? 2 : 1;

    const cellSz = layoutConfig.boardHeight / 9;
    const calculatedWidth = (layoutConfig.boardWidth / 3) * this.scaleFactor * draggingScaler;
    const calculatedHeight = cellSz * 3 * this.scaleFactor * draggingScaler;

    if (setPosition) {
      const calcPos = this.viewModel.calculateTargetPosition();
      this.targetPosX = calcPos.x * this.scaleFactor + this.scaledOffsetX;
      this.targetPosY = calcPos.y * this.scaleFactor;

      const xOffset = isDragging ? this.dragStartOffset.x : 0;
      const yOffset = isDragging ? this.dragStartOffset.y : 0;

      this.setPosition(this.targetPosX + xOffset, this.targetPosY + yOffset);
    }

    // this.debugLabel.setPosition(calculatedWidth * -0.5 + 10, calculatedHeight * -0.5 + 10);

    this.setSize(calculatedWidth, calculatedHeight);
    this.shapeRectangle.setSize(calculatedWidth, calculatedHeight);

    if (this.viewModel.currentlyValid) {
      this.setInteractive({
        hitArea: this.shapeRectangle,
        draggable: true,
        hitAreaCallback: (rect: Phaser.Geom.Rectangle, x: number, y: number) =>
          Phaser.Geom.Rectangle.Contains(rect, x, y),
      });
    }
  }

  private handleInput() {
    this.on(
      Phaser.Input.Events.DRAG_START,
      (
        pointer: Phaser.Input.Pointer,
        dragX: number,
        dragY: number,
        _gameObject: Phaser.GameObjects.Image,
      ) => {
        const layoutConfig = this.viewModel.layoutConfig;
        const cellSz = layoutConfig.boardHeight / 9;

        // Only offset the drag position for touch events
        const yOffset = pointer.wasTouch ? cellSz * 1.5 : 0;

        this.dragStartPos.setTo(this.x, this.y);
        this.dragStartOffset.setTo(dragX, dragY - yOffset);

        this.x += this.dragStartOffset.x;
        this.y += this.dragStartOffset.y;

        this.viewModel.dragStart(
          this.x,
          this.y,
          this.blockViews.map((blockView) => {
            return blockView.blockPosition(this.scaleFactor, this.scaledOffsetX, true);
          }),
        );

        //console.log(`drag start: (${this.x}, ${this.y}) | (${dragX}, ${dragY})`);
      },
    )
      .on(
        Phaser.Input.Events.DRAG,
        (_pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
          this.dragMaxDistance = Math.max(
            this.dragMaxDistance,
            Phaser.Math.Distance.Between(this.x, this.y, this.dragStartPos.x, this.dragStartPos.y),
          );
          this.hasDragged = true;

          if (!this.viewModel.currentlyDragging) {
            return;
          }

          this.x = dragX + this.dragStartOffset.x;
          this.y = dragY + this.dragStartOffset.y;

          this.viewModel.drag(
            this.x,
            this.y,
            this.blockViews.map((blockView) => {
              return blockView.blockPosition(this.scaleFactor, this.scaledOffsetX, true);
            }),
          );
        },
      )
      .on(
        Phaser.Input.Events.DRAG_END,
        (
          _pointer: Phaser.Input.Pointer,
          _dragX: number,
          _dragY: number,
          _gameObject: Phaser.GameObjects.Image,
        ) => {
          this.viewModel.dragEnd(
            this.x,
            this.y,
            this.blockViews.map((blockView) => {
              return blockView.blockPosition(this.scaleFactor, this.scaledOffsetX, true);
            }),
          );

          //console.log(`drag end`);
        },
      )
      .on(Phaser.Input.Events.POINTER_DOWN, (_pointer: Phaser.Input.Pointer) => {
        this.hasDragged = false;
        this.pointerWasDown = true;

        //console.log(`pointer down`);
      })
      .on(Phaser.Input.Events.POINTER_UP, (_pointer: Phaser.Input.Pointer) => {
        this.pointerWasDown = false;
        this.hasDragged = false;
        this.dragMaxDistance = 0;

        //console.log(`pointer up`);
      });
  }
}
